import React, { useState, useEffect, useRef } from 'react';
import { Star as StarIcon, Truck, Shield, Headphones, ArrowRight, Play, ChevronDown, MapPin, Mail, Facebook, Instagram, Youtube, Twitter, Music2, MessageCircle, Check, Heart, Zap, Clock } from 'lucide-react';
import api from '@/lib/api';
import { getProductImageUrl, resolveMediaUrl } from '@/lib/productMedia';

// ====== Individual Block Renderers ======

function HeroBlock({ content, style, primaryColor }) {
  const bgColor = content.background_color || primaryColor || '#2563eb';
  const bgImage = content.background_image;
  return (
    <div
      className="relative overflow-hidden rounded-xl"
      style={{
        background: bgImage ? `url(${bgImage}) center/cover no-repeat` : `linear-gradient(135deg, ${bgColor}, ${bgColor}dd)`,
        minHeight: 320,
        ...blockStyle(style)
      }}
    >
      {bgImage && <div className="absolute inset-0 bg-black/40" />}
      <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 py-16 min-h-[320px]">
        <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
          {content.title || 'Selamat Datang'}
        </h1>
        {content.subtitle && (
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl">{content.subtitle}</p>
        )}
        {content.button_text && (
          <a
            href={content.button_link || '#'}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold text-lg transition-all shadow-lg hover:shadow-xl hover:scale-105"
            style={{ backgroundColor: '#fff', color: bgColor }}
          >
            {content.button_text} <ArrowRight size={18} />
          </a>
        )}
      </div>
    </div>
  );
}

function ProductGridBlock({ content, style, subdomain, primaryColor }) {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    if (!subdomain) return;
    api.get(`/store/${subdomain}/products`).then(r => {
      let prods = r.data.products || r.data || [];
      if (content.category) prods = prods.filter(p => p.category === content.category);
      setProducts(prods.slice(0, content.count || 8));
    }).catch(() => {});
  }, [subdomain, content.category, content.count]);

  const cols = content.columns || 4;
  return (
    <div style={blockStyle(style)}>
      {content.title && <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">{content.title}</h2>}
      <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${Math.min(cols, 4)}, minmax(0, 1fr))` }}>
        {products.map(p => (
          <a key={p.id} href={`/store/${subdomain}/product/${p.slug || p.id}`} className="group block rounded-xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg transition-all">
            <div className="aspect-square bg-slate-100 overflow-hidden">
              {getProductImageUrl(p) ? (
                <img src={getProductImageUrl(p)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-300 text-4xl">📦</div>
              )}
            </div>
            <div className="p-3">
              <p className="font-medium text-sm text-slate-800 line-clamp-2">{p.name}</p>
              <p className="font-bold mt-1" style={{ color: primaryColor }}>
                Rp {(p.price || 0).toLocaleString('id-ID')}
              </p>
            </div>
          </a>
        ))}
      </div>
      {products.length === 0 && <p className="text-center text-slate-400 py-8">Belum ada produk</p>}
    </div>
  );
}

function TextBlock({ content, style }) {
  return (
    <div style={{ textAlign: content.alignment || 'left', ...blockStyle(style) }}>
      {content.title && <h2 className="text-2xl font-bold text-slate-900 mb-4">{content.title}</h2>}
      <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: content.content || '' }} />
    </div>
  );
}

function ImageBlock({ content, style }) {
  const widthClass = content.width === 'small' ? 'max-w-sm' : content.width === 'medium' ? 'max-w-2xl' : 'max-w-full';
  return (
    <div className={`${widthClass} mx-auto`} style={blockStyle(style)}>
      {content.image_url ? (
        content.link ? (
          <a href={content.link} target="_blank" rel="noopener noreferrer">
            <img src={content.image_url} alt={content.caption || ''} className="w-full rounded-xl" />
          </a>
        ) : (
          <img src={content.image_url} alt={content.caption || ''} className="w-full rounded-xl" />
        )
      ) : (
        <div className="w-full aspect-video bg-slate-100 rounded-xl flex items-center justify-center text-slate-400">
          Tambahkan URL gambar
        </div>
      )}
      {content.caption && <p className="text-sm text-slate-500 text-center mt-2">{content.caption}</p>}
    </div>
  );
}

function BannerBlock({ content, style, primaryColor }) {
  const bgColor = content.background_color || primaryColor || '#dc2626';
  return (
    <div className="rounded-xl px-8 py-10 text-center" style={{ backgroundColor: bgColor, ...blockStyle(style) }}>
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">{content.title || 'Promo Spesial'}</h2>
      {content.subtitle && <p className="text-white/90 text-lg mb-6">{content.subtitle}</p>}
      {content.button_text && (
        <a
          href={content.button_link || '#'}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white font-semibold transition-all hover:scale-105 shadow-lg"
          style={{ color: bgColor }}
        >
          {content.button_text} <ArrowRight size={16} />
        </a>
      )}
    </div>
  );
}

function TestimonialsBlock({ content, style }) {
  const items = content.items || [];
  return (
    <div style={blockStyle(style)}>
      {content.title && <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">{content.title}</h2>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex gap-0.5 mb-3">
              {[...Array(t.rating || 5)].map((_, j) => (
                <StarIcon key={j} size={14} className="fill-amber-400 text-amber-400" />
              ))}
            </div>
            <p className="text-slate-600 text-sm italic mb-3">"{t.text}"</p>
            <p className="font-semibold text-slate-800 text-sm">— {t.name}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CTABlock({ content, style, primaryColor }) {
  const bgColor = primaryColor || '#2563eb';
  return (
    <div className="rounded-xl px-8 py-12 text-center" style={{ background: `linear-gradient(135deg, ${bgColor}, ${bgColor}cc)`, ...blockStyle(style) }}>
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{content.title || 'Siap Memulai?'}</h2>
      {content.subtitle && <p className="text-white/90 text-lg mb-6">{content.subtitle}</p>}
      {content.button_text && (
        <a
          href={content.button_link || '#'}
          className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-white font-semibold text-lg transition-all hover:scale-105 shadow-lg"
          style={{ color: bgColor }}
        >
          {content.button_text} <ArrowRight size={18} />
        </a>
      )}
    </div>
  );
}

function FeaturesBlock({ content, style }) {
  const items = content.items || [];
  const iconMap = { truck: Truck, shield: Shield, headphones: Headphones, star: StarIcon, heart: Heart, zap: Zap, check: Check, clock: Clock };
  return (
    <div style={blockStyle(style)}>
      {content.title && <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">{content.title}</h2>}
      <div className="grid md:grid-cols-3 gap-6">
        {items.map((item, i) => {
          const Icon = iconMap[item.icon] || Shield;
          return (
            <div key={i} className="text-center p-6 rounded-xl bg-white border border-slate-200 shadow-sm">
              <div className="w-12 h-12 mx-auto mb-4 bg-blue-50 rounded-xl flex items-center justify-center">
                <Icon size={24} className="text-blue-600" />
              </div>
              <h3 className="font-bold text-slate-800 mb-2">{item.title}</h3>
              <p className="text-sm text-slate-500">{item.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SpacerBlock({ content, style }) {
  return <div style={{ height: content.height || 60, ...blockStyle(style) }} />;
}

function VideoBlock({ content, style }) {
  const url = content.video_url || '';
  const ratio = content.aspect_ratio === '4:3' ? '75%' : '56.25%';
  return (
    <div style={blockStyle(style)}>
      {content.title && <h2 className="text-2xl font-bold text-slate-900 mb-4 text-center">{content.title}</h2>}
      {url ? (
        <div className="relative rounded-xl overflow-hidden" style={{ paddingBottom: ratio }}>
          <iframe
            src={url}
            title="Video"
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <div className="w-full aspect-video bg-slate-100 rounded-xl flex items-center justify-center text-slate-400">
          <Play size={48} />
        </div>
      )}
    </div>
  );
}

function DividerBlock({ content, style }) {
  return (
    <div style={blockStyle(style)} className="flex items-center justify-center">
      <hr
        className="w-full"
        style={{
          borderStyle: content.style || 'solid',
          borderColor: content.color || '#e2e8f0',
          borderWidth: `${content.width || 1}px 0 0 0`
        }}
      />
    </div>
  );
}

function FAQBlock({ content, style }) {
  const [openIdx, setOpenIdx] = useState(null);
  const items = content.items || [];
  return (
    <div style={blockStyle(style)}>
      {content.title && <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">{content.title}</h2>}
      <div className="max-w-3xl mx-auto space-y-3">
        {items.map((item, i) => (
          <div key={i} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
            <button
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
            >
              <span className="font-semibold text-slate-800 text-sm pr-4">{item.question}</span>
              <ChevronDown size={18} className={`text-slate-400 flex-shrink-0 transition-transform ${openIdx === i ? 'rotate-180' : ''}`} />
            </button>
            {openIdx === i && (
              <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
                {item.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function GalleryBlock({ content, style }) {
  const images = content.images || [];
  const cols = content.columns || 3;
  return (
    <div style={blockStyle(style)}>
      {content.title && <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">{content.title}</h2>}
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(cols, 6)}, minmax(0, 1fr))` }}>
        {images.map((img, i) => (
          <div key={i} className="aspect-square rounded-xl overflow-hidden bg-slate-100 group">
            {img.url ? (
              <img src={img.url} alt={img.caption || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-300 text-3xl">🖼️</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingBlock({ content, style, primaryColor }) {
  const plans = content.plans || [];
  const pc = primaryColor || '#2563eb';
  return (
    <div style={blockStyle(style)}>
      {content.title && <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">{content.title}</h2>}
      <div className={`grid gap-5 ${plans.length <= 3 ? `md:grid-cols-${plans.length}` : 'md:grid-cols-3'}`} style={{ gridTemplateColumns: `repeat(${Math.min(plans.length || 1, 3)}, minmax(0, 1fr))` }}>
        {plans.map((plan, i) => (
          <div key={i} className={`rounded-2xl border-2 p-6 text-center ${plan.highlighted ? 'shadow-xl scale-105' : 'shadow-sm'}`}
            style={{ borderColor: plan.highlighted ? pc : '#e2e8f0', backgroundColor: plan.highlighted ? `${pc}05` : '#fff' }}>
            {plan.highlighted && <span className="inline-block px-3 py-1 rounded-full text-xs font-bold text-white mb-3" style={{ backgroundColor: pc }}>POPULER</span>}
            <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
            <div className="my-4">
              <span className="text-3xl font-extrabold" style={{ color: pc }}>Rp {plan.price}</span>
              <span className="text-slate-500 text-sm">{plan.period}</span>
            </div>
            <ul className="space-y-2 mb-6 text-sm text-left">
              {(plan.features || []).map((f, j) => (
                <li key={j} className="flex items-center gap-2 text-slate-600"><Check size={14} style={{ color: pc }} className="flex-shrink-0" /> {f}</li>
              ))}
            </ul>
            {plan.button_text && (
              <a href={plan.button_link || '#'} className={`block w-full py-2.5 rounded-full font-semibold text-sm transition-all hover:opacity-90 ${plan.highlighted ? 'text-white' : ''}`}
                style={{ backgroundColor: plan.highlighted ? pc : `${pc}10`, color: plan.highlighted ? '#fff' : pc }}>
                {plan.button_text}
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CountdownBlock({ content, style, primaryColor }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: false });
  useEffect(() => {
    if (!content.target_date) return;
    const target = new Date(content.target_date).getTime();
    const tick = () => {
      const now = Date.now();
      const diff = target - now;
      if (diff <= 0) { setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true }); return; }
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
        expired: false
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [content.target_date]);
  const pc = primaryColor || '#2563eb';
  const units = [
    { label: 'Hari', value: timeLeft.days },
    { label: 'Jam', value: timeLeft.hours },
    { label: 'Menit', value: timeLeft.minutes },
    { label: 'Detik', value: timeLeft.seconds }
  ];
  return (
    <div className="text-center rounded-xl py-10 px-6" style={{ background: `linear-gradient(135deg, ${pc}10, ${pc}05)`, ...blockStyle(style) }}>
      {content.title && <h2 className="text-2xl font-bold text-slate-900 mb-6">{content.title}</h2>}
      {timeLeft.expired ? (
        <p className="text-lg text-slate-500">{content.expired_text || 'Waktu telah habis!'}</p>
      ) : !content.target_date ? (
        <p className="text-slate-400">Set tanggal target di pengaturan</p>
      ) : (
        <div className="flex justify-center gap-4">
          {units.map(u => (
            <div key={u.label} className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 min-w-[80px]">
              <div className="text-3xl font-extrabold" style={{ color: pc }}>{String(u.value).padStart(2, '0')}</div>
              <div className="text-xs text-slate-500 mt-1 font-medium">{u.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatsBlock({ content, style, primaryColor }) {
  const items = content.items || [];
  const pc = primaryColor || '#2563eb';
  return (
    <div style={blockStyle(style)}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((item, i) => (
          <div key={i} className="text-center py-6">
            <div className="text-3xl md:text-4xl font-extrabold mb-1" style={{ color: pc }}>{item.value}</div>
            <div className="text-sm text-slate-500 font-medium">{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MapBlock({ content, style }) {
  return (
    <div style={blockStyle(style)}>
      {content.title && <h2 className="text-2xl font-bold text-slate-900 mb-4 text-center">{content.title}</h2>}
      {content.embed_url ? (
        <div className="rounded-xl overflow-hidden" style={{ height: content.height || 400 }}>
          <iframe src={content.embed_url} title="Map" className="w-full h-full border-0" allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </div>
      ) : (
        <div className="w-full rounded-xl bg-slate-100 flex flex-col items-center justify-center text-slate-400" style={{ height: content.height || 400 }}>
          <MapPin size={48} className="mb-2" />
          <p className="text-sm">Tambahkan Google Maps embed URL</p>
        </div>
      )}
    </div>
  );
}

function SocialBlock({ content, style, primaryColor }) {
  const links = content.links || [];
  const pc = primaryColor || '#2563eb';
  const iconMap = { instagram: Instagram, facebook: Facebook, youtube: Youtube, twitter: Twitter, tiktok: Music2, whatsapp: MessageCircle };
  const colorMap = { instagram: '#E4405F', facebook: '#1877F2', youtube: '#FF0000', twitter: '#1DA1F2', tiktok: '#000000', whatsapp: '#25D366' };
  return (
    <div className="text-center" style={blockStyle(style)}>
      {content.title && <h2 className="text-2xl font-bold text-slate-900 mb-6">{content.title}</h2>}
      <div className="flex justify-center gap-4 flex-wrap">
        {links.filter(l => l.url).map((link, i) => {
          const Icon = iconMap[link.platform] || MessageCircle;
          const bgColor = colorMap[link.platform] || pc;
          return (
            <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
              className="w-12 h-12 rounded-full flex items-center justify-center text-white transition-all hover:scale-110 hover:shadow-lg"
              style={{ backgroundColor: bgColor }}>
              <Icon size={22} />
            </a>
          );
        })}
        {links.filter(l => l.url).length === 0 && <p className="text-slate-400 text-sm">Tambahkan link sosial media</p>}
      </div>
    </div>
  );
}

function TeamBlock({ content, style }) {
  const members = content.members || [];
  return (
    <div style={blockStyle(style)}>
      {content.title && <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">{content.title}</h2>}
      <div className="grid md:grid-cols-3 gap-6">
        {members.map((m, i) => (
          <div key={i} className="text-center bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-slate-100 mb-4">
              {m.photo_url ? (
                <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-300 text-2xl">👤</div>
              )}
            </div>
            <h3 className="font-bold text-slate-800">{m.name}</h3>
            <p className="text-sm text-slate-500">{m.role}</p>
            {m.bio && <p className="text-xs text-slate-400 mt-2">{m.bio}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function LogosBlock({ content, style }) {
  const logos = content.logos || [];
  return (
    <div className="text-center" style={blockStyle(style)}>
      {content.title && <h2 className="text-xl font-bold text-slate-900 mb-6">{content.title}</h2>}
      <div className="flex flex-wrap justify-center items-center gap-8">
        {logos.map((logo, i) => (
          <div key={i} className="flex-shrink-0">
            {logo.image_url ? (
              logo.link ? (
                <a href={logo.link} target="_blank" rel="noopener noreferrer"><img src={logo.image_url} alt={logo.name || ''} className="h-12 max-w-[140px] object-contain opacity-70 hover:opacity-100 transition-opacity" /></a>
              ) : (
                <img src={logo.image_url} alt={logo.name || ''} className="h-12 max-w-[140px] object-contain opacity-70" />
              )
            ) : (
              <div className="h-12 w-24 bg-slate-100 rounded-lg flex items-center justify-center text-xs text-slate-400">{logo.name}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function NewsletterBlock({ content, style, primaryColor }) {
  const pc = primaryColor || '#2563eb';
  return (
    <div className="text-center rounded-xl py-12 px-6" style={{ background: `linear-gradient(135deg, ${pc}10, ${pc}05)`, ...blockStyle(style) }}>
      {content.title && <h2 className="text-2xl font-bold text-slate-900 mb-2">{content.title}</h2>}
      {content.subtitle && <p className="text-slate-500 mb-6 max-w-lg mx-auto">{content.subtitle}</p>}
      <div className="flex max-w-md mx-auto gap-2">
        <input type="email" placeholder={content.placeholder || 'Email Anda'} className="flex-1 px-4 py-2.5 rounded-full border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
        <button className="px-6 py-2.5 rounded-full text-white font-semibold text-sm transition-all hover:opacity-90 shadow-sm" style={{ backgroundColor: pc }}>
          {content.button_text || 'Subscribe'}
        </button>
      </div>
    </div>
  );
}

// ====== Style Helper ======

function blockStyle(style = {}) {
  const s = {};
  if (style.paddingTop) s.paddingTop = style.paddingTop;
  if (style.paddingBottom) s.paddingBottom = style.paddingBottom;
  if (style.marginTop) s.marginTop = style.marginTop;
  if (style.marginBottom) s.marginBottom = style.marginBottom;
  if (style.backgroundColor) s.backgroundColor = style.backgroundColor;
  if (style.textColor) s.color = style.textColor;
  if (style.borderRadius) s.borderRadius = style.borderRadius;
  if (style.maxWidth) s.maxWidth = style.maxWidth;
  if (style.backgroundImage) {
    s.backgroundImage = `url(${style.backgroundImage})`;
    s.backgroundSize = 'cover';
    s.backgroundPosition = 'center';
  }
  return s;
}

// ====== Custom HTML Block ======

function CustomHtmlBlock({ content, style }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !content.html_code) return;
    const container = containerRef.current;
    container.innerHTML = '';
    const range = document.createRange();
    range.selectNode(container);
    const fragment = range.createContextualFragment(content.html_code);
    container.appendChild(fragment);
    return () => { container.innerHTML = ''; };
  }, [content.html_code]);

  return (
    <div style={blockStyle(style)}>
      <div ref={containerRef} />
    </div>
  );
}

function BookingServicesBlock({ content, style, subdomain, primaryColor }) {
  const [services, setServices] = useState([]);
  useEffect(() => {
    if (!subdomain) return;
    api.get(`/store/${subdomain}/booking-services`).then(r => {
      setServices((r.data.services || []).slice(0, content.count || 6));
    }).catch(() => {});
  }, [subdomain, content.count]);

  const cols = content.columns || 3;
  return (
    <div style={blockStyle(style)}>
      {content.title && <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">{content.title}</h2>}
      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(cols, 4)}, minmax(0, 1fr))` }}>
        {services.map(svc => (
          <a key={svc.id} href={`/store/${subdomain}/booking/${svc.id}`} className="group block rounded-xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg transition-all">
            <div className="h-40 bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden flex items-center justify-center">
              {svc.images?.[0] ? <img src={svc.images[0]} alt={svc.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <span className="text-4xl">🚗</span>}
            </div>
            <div className="p-3">
              <p className="font-medium text-sm text-slate-800 line-clamp-1">{svc.name}</p>
              {svc.location && <p className="text-xs text-slate-500 mt-0.5">📍 {svc.location}</p>}
              <p className="font-bold mt-1" style={{ color: primaryColor }}>
                Rp {(svc.price || 0).toLocaleString('id-ID')} <span className="text-xs font-normal text-slate-400">{svc.price_unit}</span>
              </p>
            </div>
          </a>
        ))}
      </div>
      {services.length === 0 && <p className="text-center text-slate-400 py-8">Belum ada layanan booking</p>}
    </div>
  );
}

// ====== Block Type Map ======

const BLOCK_COMPONENTS = {
  hero: HeroBlock,
  products: ProductGridBlock,
  text: TextBlock,
  image: ImageBlock,
  banner: BannerBlock,
  testimonials: TestimonialsBlock,
  cta: CTABlock,
  features: FeaturesBlock,
  spacer: SpacerBlock,
  video: VideoBlock,
  divider: DividerBlock,
  faq: FAQBlock,
  gallery: GalleryBlock,
  pricing: PricingBlock,
  countdown: CountdownBlock,
  stats: StatsBlock,
  map: MapBlock,
  social: SocialBlock,
  team: TeamBlock,
  logos: LogosBlock,
  newsletter: NewsletterBlock,
  html: CustomHtmlBlock,
  booking_services: BookingServicesBlock
};

// ====== Main BlockRenderer ======

export default function BlockRenderer({ blocks = [], subdomain = '', primaryColor = '#2D6CDF' }) {
  return (
    <div className="space-y-0">
      {blocks.filter(b => b.is_visible !== false).sort((a, b) => a.order - b.order).map(block => {
        const Component = BLOCK_COMPONENTS[block.type];
        if (!Component) return null;
        return (
          <div key={block.id} className="page-block">
            <Component
              content={block.content || {}}
              style={block.style || {}}
              primaryColor={primaryColor}
              subdomain={subdomain}
            />
          </div>
        );
      })}
    </div>
  );
}

export { BLOCK_COMPONENTS, blockStyle };
